import { databases, storage } from '../lib/appwrite';
import { ID, Permission, Role, Query } from 'appwrite';
import { getProfileByUserId, updateWalletBalance } from './profileService';

// ============================================
// MANUAL TOP UP SERVICE
// ============================================

const DATABASE_ID = process.env.REACT_APP_APPWRITE_DATABASE_ID;
// Using placeholder for now, assuming it will be set in .env or hardcoded if not found
export const MANUAL_TOPUP_COLLECTION_ID = process.env.REACT_APP_APPWRITE_TEMPORARY_TOPUP_COLLECTION_ID || 'temporary_topup';
const BUCKET_ID = process.env.REACT_APP_APPWRITE_BUCKET_ID;

/**
 * Upload a payment receipt to Appwrite Storage
 * @param {File} file - The receipt image file
 * @returns {Promise<string>} - The uploaded file ID
 */
export const uploadReceipt = async (file) => {
    try {
        if (!BUCKET_ID) {
            throw new Error('BUCKET_ID is not configured');
        }

        if (!file) return null;

        const response = await storage.createFile(
            BUCKET_ID,
            ID.unique(),
            file
        );

        return response.$id;
    } catch (error) {
        console.error('Error uploading receipt:', error);
        throw error;
    }
};

/**
 * Get the view URL for a receipt image
 * @param {string} fileId - The file ID from Appwrite Storage
 * @returns {string} - The public preview URL
 */
export const getReceiptUrl = (fileId) => {
    if (!fileId || !BUCKET_ID) return '';
    return storage.getFileView(BUCKET_ID, fileId).toString();
};

/**
 * Create a new manual top-up request
 * @param {Object} data - The top-up request data
 * @param {string} userId - The user's ID
 * @returns {Promise<Object>} - Created document
 */
export const createManualTopUpRequest = async (data, userId) => {
    try {
        if (!DATABASE_ID || !MANUAL_TOPUP_COLLECTION_ID) {
            throw new Error('Database or Collection ID not configured for manual top-ups');
        }

        const doc = await databases.createDocument(
            DATABASE_ID,
            MANUAL_TOPUP_COLLECTION_ID,
            ID.unique(),
            {
                user_id: userId,
                amount: data.amount.toString(), // collection expect Enum as 15, 25, 50 string
                receipt_id: data.receiptId || null,
                status: 'pending'
            },
            [
                Permission.read(Role.user(userId)),
                Permission.update(Role.user(userId)),
                Permission.delete(Role.user(userId))
            ]
        );

        return doc;
    } catch (error) {
        console.error('Error creating manual top-up request:', error);
        throw error;
    }
};

/**
 * Get all manual top-up requests for a user
 * @param {string} userId - The user's ID
 * @returns {Promise<Array>} - List of top-up requests
 */
export const getManualTopUpsForUser = async (userId) => {
    try {
        if (!DATABASE_ID || !MANUAL_TOPUP_COLLECTION_ID) {
            throw new Error('Database or Collection ID not configured');
        }

        const response = await databases.listDocuments(
            DATABASE_ID,
            MANUAL_TOPUP_COLLECTION_ID,
            [
                Query.equal('user_id', userId),
                Query.orderDesc('$createdAt')
            ]
        );

        return response.documents;
    } catch (error) {
        console.error('Error fetching manual top-ups:', error);
        throw error;
    }
};

/**
 * Approve a manual top-up request (Admin-only logic)
 * Updates the top-up status to 'completed' and increments the user's wallet balance.
 * @param {string} requestId - The ID of the top-up request
 * @returns {Promise<Object>} - Updated request document
 */
export const approveManualTopUp = async (requestId) => {
    try {
        // 1. Get the top-up request
        const request = await databases.getDocument(DATABASE_ID, MANUAL_TOPUP_COLLECTION_ID, requestId);
        if (!request) throw new Error('Top-up request not found');
        if (request.status === 'completed') throw new Error('Request already completed');

        // 2. Update user's wallet balance
        const profile = await getProfileByUserId(request.user_id);
        if (!profile) throw new Error('User profile not found');

        const currentBalance = parseFloat(profile.wallet_balance || 0);
        const topUpAmount = parseFloat(request.amount);
        const newBalance = currentBalance + topUpAmount;

        await updateWalletBalance(profile.$id, newBalance);

        // 3. Update top-up request status to completed
        const updatedRequest = await databases.updateDocument(
            DATABASE_ID,
            MANUAL_TOPUP_COLLECTION_ID,
            requestId,
            { status: 'completed' }
        );

        return updatedRequest;
    } catch (error) {
        console.error('Error approving manual top-up:', error);
        throw error;
    }
};

export default {
    uploadReceipt,
    getReceiptUrl,
    createManualTopUpRequest,
    getManualTopUpsForUser,
    approveManualTopUp
};
